
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import { cloneDeep, isEqual } from "lodash-es";

  import LIST_USER_APPLICATIONS from "@/apollo/jobboard/ListUserApplications.gql";
  import DETACH_APPLICATION_DOC from "@/apollo/applications/DetachApplicationDoc.gql";
  import REMOVE_APPLICATION_COMMENTS from "@/apollo/applications/RemoveApplicationComments.gql";

  import CardPagination from "@/components/card/CardPagination.vue";
  import JobBoardApplicationCard from "@/components/jobboard/JobBoardApplicationCard.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import JobBoardApplicationsFilters from "@/components/jobboard/JobBoardApplicationsFilters.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

  import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";

  import paginationConfig from "@/data/paginationConfig";
  import applicationFiltersTemplate from "@/data/filtersTemplates/applicationFiltersTemplate";

  import { Application, Posting, Query_RootApplicationArgs, User } from "@/generated/graphql";
  import PaginationConfig from "@/entities/PaginationConfig";
  import ApplicationFilters from "@/entities/filters/ApplicationFilters";
  import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
  import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
  import { NOTIFICATION } from "@/entities/Events";
  import { defaultErrorNotificationMessage } from "@/data/errorMessages";
  import Message from "@/data/notificationsMessages";
  import { NO_INFORMATION } from "@/data/placeholders";
  import EmptyListImageApplications from "@/components/icons/emptyListImages/EmptyListImageApplications.vue";

  @Component({
    components: {
      EmptyListImageApplications,
      CardPagination,
      JobBoardApplicationCard,
      LoadingOverlay,
      EmptyListPlaceholder,
      DefaultContentLayout,
      JobBoardApplicationsFilters,
    },
    apollo: {
      applications: {
        query: LIST_USER_APPLICATIONS,
        variables() {
          return this.queryArgs;
        },
        update(data) {
          this.paginationConfig.total = data.total.aggregate.count;
          this.paginationConfig.loadedCount = data.application.length;
          return data.application;
        },
      },
    },
  })
  export default class JobBoardApplications extends Vue {
    @Prop({ type: Object, required: true }) currentUser!: User;

    applications: Application[] | null = null;
    paginationConfig: PaginationConfig = { ...paginationConfig };
    processing: boolean = false;

    filters: ApplicationFilters = cloneDeep(applicationFiltersTemplate);

    get isLoading(): boolean {
      return this.$apollo.queries.applications.loading || this.processing;
    }

    get emptyListMessage(): string {
      const filtersEmpty = isEqual(this.filters, applicationFiltersTemplate);

      if (this.filterByDocumentId) {
        return "No applications";
      }

      if (filtersEmpty) {
        return "Start applying for Jobs in postings";
      }

      return getEmptyFiltersResultMessage("jobs");
    }
    
    get emptyListSubtitle(): string{
      return "Go to Postings to review open jobs";
    }

    get isApplicationsExist(): boolean {
      if (!this.applications) {
        return false;
      }
      return this.applications.length > 0;
    }

    get filterByDocumentId() {
      return this.$route.query.filter_by_document_id;
    }

    getPostingLocation(posting: Posting): string {
      const { country, city } = posting;
      const location = [city, country].filter(place => Boolean(place)).join(" ");
      return location || NO_INFORMATION;
    }

    get queryArgs(): Query_RootApplicationArgs {
      const { page, limit } = this.paginationConfig;
      const offset = page * limit - limit;

      if (this.filterByDocumentId) {
        return {
          offset,
          limit,
          where: {
            _and: [
              {
                user_id: {
                  _eq: this.currentUser.id,
                },
              },
              {
                document_id: {
                  _eq: this.filterByDocumentId,
                },
              },
              this.filtersConditions,
            ],
          },
        }
      }

      return {
        offset,
        limit,
        where: {
          _and: [
            {
              user_id: { _eq: this.currentUser.id },
            },
            {
              is_cancelled: { _eq: false }
            },
            this.filtersConditions,
          ],
        },
      };
    }

    get filtersConditions(): Record<string, any> {
      const conditions = [];

      if (this.filters.countries.length > 0) {
        const countries = this.filters.countries.map(countryName => ({
          posting: {
            country: {
              _eq: countryName,
            },
          },
        }));
        conditions.push({ _or: countries });
      }

      if (this.filters.states.length > 0) {
        const states = this.filters.states.map(stateName => ({
          posting: {
            state: {
              _eq: stateName,
            },
          },
        }));
        conditions.push({ _or: states });
      }

      if (this.filters.cities.length > 0) {
        const cities = this.filters.cities.map(cityName => ({
          posting: {
            city: {
              _eq: cityName,
            },
          },
        }));
        conditions.push({ _or: cities });
      }

      if (this.filters.companies.length > 0) {
        conditions.push({
          posting: {
            company: {
              id: {
                _in: this.filters.companies,
              }
            }
          },
        });
      }

      if (this.filters.employmentTypes.length > 0) {
        const employmentTypes = this.filters.employmentTypes.map(employmentType => ({
          posting: {
            employment_type: { _eq: employmentType },
          },
        }));
        conditions.push({ _or: employmentTypes });
      }

      if (this.filters.skills.length > 0) {
        const skills = this.filters.skills.map(skillId => ({
          posting: {
            skills: {
              skill_id: { _in: skillId },
            },
          },
        }));
        conditions.push({ _or: skills });
      }

      if (conditions.length === 0) {
        return {};
      }

      return { _and: conditions };
    }

    get queryPropsToWatch(): string {
      return JSON.stringify([
        this.filtersConditions,
        this.paginationConfig,
      ]);
    }

    get queryParams(): {filters: ApplicationFilters, pagination: PaginationConfig } {
      const query = this.$route.query;

      const {
        min_rate: minRate,
        max_rate: maxRate,
        employment_types: employmentTypes,
        ...queryParams
      } = query;

      const queryData = {
        minRate,
        maxRate,
        employmentTypes,
        ...queryParams,
      };

      const filters = getFiltersDataFromQuery<ApplicationFilters>(applicationFiltersTemplate, queryData);
      const pagination = {...this.paginationConfig, ...getPaginationDataFromQuery(query)};

      return {
        filters,
        pagination,
      };
    }

    get isChosenPageEmpty(): boolean {
      const collectionEmpty = this.applications?.length === 0;
      const notFirstPage = this.paginationConfig.page !== 1;

      return !this.isLoading && collectionEmpty && notFirstPage;
    }

    @Watch("queryPropsToWatch")
    updateQuery(): void {
      if (this.isChosenPageEmpty) {
        return this.showFirstPage();
      }

      const paginationQuery = getPaginationQuery(this.paginationConfig);

      const {
        employmentTypes,
        ...filtersData
      } = this.filters;

      const query = joinQueryValues({
        ...this.$route.query,
        ...paginationQuery,
        ...filtersData,
        employment_types: employmentTypes,
      });

      if (isEqual(query, this.$route.query)) {
        return;
      }

      this.$router.push({query}).catch(()=>{});
    }

    @Watch("$route.query", { immediate: true })
    onQueryChange(): void {
      const { filters, pagination } = this.queryParams;

      this.filters = filters;
      this.paginationConfig = pagination;
    }

    showFirstPage(): void {
      this.paginationConfig = {...this.paginationConfig, page: paginationConfig.page };
    }

    updateFilters(filters: ApplicationFilters): void {
      const dataEqual = isEqual(filters, this.filters);

      if (dataEqual) {
        return;
      }

      this.filters = filters;
    }

    resetFilters(): void {
      if (isEqual(this.filters, applicationFiltersTemplate)) {
        return;
      }

      this.filters = cloneDeep(applicationFiltersTemplate);
      this.paginationConfig = { ...paginationConfig };
    }

    async detachDocument(applicationId: string): Promise<void> {
      if (this.isLoading) {
        return;
      }

      this.processing = true;

      try {
        await this.$apollo.mutate({
          mutation: DETACH_APPLICATION_DOC,
          variables: {
            applicationId,
          },
        });
        this.refetchApplications();
      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorNotificationMessage));
      } finally {
        this.processing = false;
      }
    }

    async removeComments(applicationId: string): Promise<void> {
      if (this.isLoading) {
        return;
      }

      this.processing = true;

      try {
        await this.$apollo.mutate({
          mutation: REMOVE_APPLICATION_COMMENTS,
          variables: {
            applicationId,
          },
        });

        this.$eventBus.emit(NOTIFICATION, Message.successMessage("Comments was removed"));
        this.refetchApplications();
      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorNotificationMessage));
      } finally {
        this.processing = false;
      }
    }

    refetchApplications(): void {
      this.$apollo.queries.applications.refetch();
    }
  }
